import { useEffect, useRef, useState } from "react"

const identity = (value: string) => value

export function useDebouncedField(
  value: string,
  onCommit: (value: string) => void,
  delay = 300,
  normalize: (value: string) => string = identity,
) {
  const [draft, setDraft] = useState(value)
  const draftRef = useRef(value)
  const onCommitRef = useRef(onCommit)
  const normalizeRef = useRef(normalize)
  const timerRef = useRef<number | undefined>(undefined)

  onCommitRef.current = onCommit
  normalizeRef.current = normalize

  function commit() {
    if (timerRef.current === undefined) return

    window.clearTimeout(timerRef.current)
    timerRef.current = undefined
    const normalized = normalizeRef.current(draftRef.current)
    draftRef.current = normalized
    setDraft(normalized)
    onCommitRef.current(normalized)
  }

  function change(next: string) {
    draftRef.current = next
    setDraft(next)

    if (timerRef.current !== undefined) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(commit, delay)
  }

  useEffect(
    () => () => {
      if (timerRef.current === undefined) return

      window.clearTimeout(timerRef.current)
      timerRef.current = undefined
      onCommitRef.current(normalizeRef.current(draftRef.current))
    },
    [],
  )

  return { value: draft, onChange: change, flush: commit }
}
